import React, { useState, useMemo } from 'react';
import {
    IconButton,
    Chip,
    Stack,
    Box,
    Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CommonTable from '../UI/Table/CommonTable';

/**
 * GrowthSessionTable component for displaying and managing growth session data
 */
const GrowthSessionTable = ({
    tableData = [],
    handleEdit,
    handleDelete,
    handleView,
    handleFeedback,
    isLoading = false,
    actionButtons = null
}) => {
    // State management
    const [selectedSession, setSelectedSession] = useState(null);

    // Memoized column definitions
    const columns = useMemo(() => [
        { key: 'serialNo', label: 'S.No', minWidth: 70, align: 'center' },
        { key: 'participant_name', label: 'Participant', minWidth: 150 },
        { key: 'lead_name', label: 'Lead', minWidth: 150 },
        { key: 'session_date', label: 'Session Date', minWidth: 120 },
        { key: 'session_type', label: 'Type', minWidth: 120 },
        { key: 'status', label: 'Status', minWidth: 100 },
        { key: 'actions', label: 'Actions', minWidth: 150 }
    ], []);

    // Helper functions
    const handleStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'success';
            case 'scheduled': return 'info';
            case 'cancelled': return 'error';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const handleTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'one-on-one': return { bg: '#e3f2fd', color: '#1976d2' };
            case 'group': return { bg: '#f3e5f5', color: '#7b1fa2' };
            case 'feedback': return { bg: '#fff8e1', color: '#f9a825' };
            case 'review': return { bg: '#fbe9e7', color: '#d84315' };
            default: return { bg: '#f5f5f5', color: '#666' };
        }
    };

    const confirmDelete = (sessionId, participantName) => {
        if (window.confirm(`Are you sure you want to delete the growth session for "${participantName}"?`)) {
            handleDelete(sessionId);
        }
    };

    const handleRowSelect = (session) => {
        setSelectedSession(selectedSession?.id === session.id ? null : session);
    };

    // Cell rendering
    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 500 }}>
                        {index + 1}
                    </Box>
                );

            case 'participant_name':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: 400,
                        color: '#333',
                        fontSize: '13px',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'lead_name':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: 400,
                        color: '#333',
                        fontSize: '13px',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'session_date':
                return (
                    <Typography variant="body2" sx={{
                        fontSize: '12px',
                        color: '#666',
                        fontFamily: 'Poppins'
                    }}>
                        {value ? new Date(value).toLocaleDateString() : 'N/A'}
                    </Typography>
                );

            case 'session_type':
                const typeStyle = handleTypeColor(value);
                return (
                    <Chip
                        label={value || 'N/A'}
                        size="small"
                        sx={{
                            bgcolor: typeStyle.bg,
                            color: typeStyle.color,
                            fontWeight: 600,
                            borderRadius: 2,
                            fontSize: '0.7rem',
                            height: 24,
                            fontFamily: 'Poppins'
                        }}
                    />
                );

            case 'status':
                return (
                    <Chip
                        label={value || 'N/A'}
                        size="small"
                        color={handleStatusColor(value)}
                        sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                            fontFamily: 'Poppins'
                        }}
                    />
                );

            case 'actions':
                return (
                    <Stack direction="row" spacing={0.5}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleView(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <VisibilityIcon sx={{ fontSize: 16, color: '#666' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <EditIcon sx={{ fontSize: 16, color: '#666' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleFeedback(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <FeedbackIcon sx={{ fontSize: 16, color: '#666' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(row.id, row.participant_name);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 16, color: '#666' }} />
                        </IconButton>
                    </Stack>
                );
            default:
                return value || 'N/A';
        }
    };

    return (
        <CommonTable
            columns={columns}
            data={tableData}
            renderCustomCell={renderCell}
            onRowClick={handleRowSelect}
            selectedRow={selectedSession}
            actionButtons={actionButtons}
            isLoading={isLoading}
            searchPlaceholder="Search growth sessions..."
        />
    );
};

export default GrowthSessionTable;